// Provenance: repo:tds-reconciliation-portal:frontend/src/api/client.ts (ADR-0005) — REBUILT.
// The old transport was cookie-authenticated against a bare-JSON backend; this one rides
// `session.authed` from @fiveox/shell (Bearer + one serialized refresh + the one place a
// session ends), adds the estate's org header, unwraps the constitutional envelope, and maps
// old-style paths onto `/api/v1`. The DECISION modules import exactly the old surface —
// `api`, `postJson`, `patchJson`, `apiUrl`, `ApiError` — so they cross unchanged (T7.1).

import { ApiError, getOrgId } from '../lib/api'
import { SERVICE_API } from '../lib/config'
import { session } from '../lib/session'

export { ApiError }

/** Absolute URL for an API path — kept for compat, but a bare `<a href>` can
 *  no longer download from it: the service wants a Bearer and the org header,
 *  which an anchor cannot carry. File-producing links go through
 *  {@link downloadFile} instead. */
export function apiUrl(path: string): string {
  return `${SERVICE_API}/api/v1${path}`
}

interface Envelope<T> {
  success: boolean
  data: T
  error?: { code: string; message: string; details?: Record<string, unknown> }
}

function orgHeaders(extra?: HeadersInit): Record<string, string> {
  // a plain object, never a Headers instance — @fiveox/shell merges with a
  // spread, and spreading Headers yields {} (PLATFORM_INTEGRATION §9.3)
  const headers: Record<string, string> = {}
  for (const [k, v] of new Headers(extra).entries()) headers[k] = v
  const org = getOrgId()
  if (org) headers['X-Fiveox-Org'] = org
  return headers
}

async function parseEnvelope<T>(res: Response): Promise<T> {
  if (res.status === 204 || res.status === 205) return undefined as T
  const body = (await res.json().catch(() => null)) as Envelope<T> | null
  if (!res.ok || !body?.success) {
    throw new ApiError(
      body?.error?.code ?? 'INTERNAL',
      body?.error?.message ?? (res.statusText || `HTTP ${res.status}`),
      res.status,
      body?.error?.details ?? {},
    )
  }
  return body.data
}

/** The old `api<T>`: returns the DATA (envelope unwrapped). `init.body` may be
 *  a FormData (multipart evidence upload) — passed through untouched so the
 *  browser writes the boundary itself. */
export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = orgHeaders(init?.headers)
  if (init?.body !== undefined && !(init.body instanceof FormData)) {
    headers['content-type'] ??= 'application/json'
  }
  const res = await session.authed(apiUrl(path), { ...init, headers })
  return parseEnvelope<T>(res)
}

export function postJson<T>(path: string, body: unknown): Promise<T> {
  return api<T>(path, { method: 'POST', body: JSON.stringify(body) })
}

export function patchJson<T>(path: string, body: unknown): Promise<T> {
  return api<T>(path, { method: 'PATCH', body: JSON.stringify(body) })
}

export function deleteJson<T>(path: string): Promise<T> {
  return api<T>(path, { method: 'DELETE' })
}

/** A step-up-gated POST: the review verbs (approve / reject / reverse and the
 *  reversal verdicts) refuse without a fresh assertion from core-mfa. */
export function postWithStepUp<T>(path: string, body: unknown, assertion: string): Promise<T> {
  return api<T>(path, {
    method: 'POST',
    body: body === undefined ? undefined : JSON.stringify(body),
    headers: { 'X-StepUp-Assertion': assertion },
  })
}

/** Authenticated file download. The predecessor handed `<a href>` a cookie-
 *  authenticated URL; a Bearer world fetches the bytes and hands the browser
 *  a blob — filename from Content-Disposition, exactly what the server chose
 *  (assessee-first, so the file is self-identifying in Downloads). */
export async function downloadFile(path: string): Promise<void> {
  // reportLinks builds ABSOLUTE hrefs (apiUrl already applied) — accept both
  const target = /^https?:/.test(path) ? path : apiUrl(path)
  const res = await session.authed(target, { headers: orgHeaders() })
  if (!res.ok) {
    // error responses from the export endpoints are the JSON envelope
    await parseEnvelope(res)
    throw new ApiError('INTERNAL', `HTTP ${res.status}`, res.status)
  }
  const dispo = res.headers.get('content-disposition') ?? ''
  const match = /filename="([^"]+)"/.exec(dispo)
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = match?.[1] ?? 'download'
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/** The presigned-upload flow — the original file as it was uploaded. Same
 *  shape as {@link downloadDocument}: the service presigns (and logs the
 *  read), the browser fetches from the bucket. */
export async function downloadUpload(uploadId: string): Promise<void> {
  const { url, filename } = await api<{ url: string; filename: string }>(
    `/uploads/${uploadId}/download`,
  )
  const res = await fetch(url)
  if (!res.ok) throw new ApiError('NOT_FOUND', 'The stored file is no longer available.', 404)
  const blob = await res.blob()
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = filename
  a.click()
  URL.revokeObjectURL(a.href)
}

/** The presigned-document flow: ask the service for a short-lived URL (the
 *  read is logged server-side, constitution §4), then let the browser fetch
 *  the bytes straight from the bucket. */
export async function downloadDocument(documentId: string): Promise<void> {
  const { url, filename } = await api<{ url: string; filename: string }>(
    `/documents/${documentId}/download`,
  )
  const res = await fetch(url)
  if (!res.ok) throw new ApiError('NOT_FOUND', 'The stored file is no longer available.', 404)
  const blob = await res.blob()
  const objectUrl = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = objectUrl
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(objectUrl)
}
